import {IAttributes, IController, IOnChangesObject, IPromise, IRootScopeService, ISCEService, ITimeoutService} from "angular";
import {l} from "types/typescriptdefinisjoner_lederplan";
import {Autentisering} from "felles/tjenester/Autentisering";
import {CriiptoOIDCRessurs} from "felles/tjenester/ressurser/CriiptoOIDCRessurs";
import {Debugger} from "debug";
import _ from "lodash";
import {changesInneholder} from "../../stoettefunk/angularHjelpemetoder";
import {MeldingTilBruker, Varighet} from "../../tjenester/MeldingTilBruker";
import {gettextCatalog} from "angular-gettext";


const debug: Debugger = require('debug')('CriiptoAutentiseringBasis');
export const noBankidLogo = require('./bilder/logo_nobankid.png');
export const seBankidLogo = require('./bilder/logo_sebankid.jpg');
declare var cordova:any;

/**
 * Håndterer autentisering via criipto.io openid.
 *
 * Merk at gui for autentisering (gui for de ulike bankid criipto støtter) blir
 * vist i aktivt nettleservindu med mindre gui=popup, da blir criipto gui vist i nytt nettleservindu
 *
 * Ifbm autentiseringsprosessen vil criipto instruere nettleseren til å laste
 * det som omtales som redirect_uri (denne adressen angis av lederplan i respons fra criiptoOIDCRessurs.authorizeUrl).
 *
 * Siden som redirect_uri peker til er ansvarlig for at parametrene (CriiptoCallbackParams) criipto legger til URLen
 * propagerer til CriiptoAutentiseringBasis.
 * Dette skjer på to forskjellige måter:
 *
 * Ved modus=erstatt:
 * Aktivt nettleservindu redirectes til Criipto gui, criipto vil så redirecte til callbackurl som peker til en
 * side i lederplan webaplikasjon som er ansvarlig for å kalle CriiptoAutentiseringBasis med criiptoCallbackparams satt
 * som så trigger {@link #$onChanges}
 *
 * Ved modus=popup:
 * Criipto gui blir lastet i nytt vindu (popup), criipto vil så redirecte til callbackurl som peker til en
 * enkel html side servert av lederplan server som via window.opener.postMessage sender melding til
 * CriiptoAutentiseringBasis {@link #messageEventHandler} (se criiptoAutentisertRespons.html i lederplan tjenester).
 *
 * Ved modus=cordova:
 * Criipto gui blir lastet i nytt vindu (popup) via cordova inapp browser, criipto vil så redirecte til callbackurl som peker til en
 * enkel html side servert av lederplan server som via cordova_iab.postMessage sender melding til
 * CriiptoAutentiseringBasis {@link #cordovaIabMessageEventHandler} (se criiptoAutentisertRespons.html i lederplan tjenester).
 *
 * Se /dokumentasjon/integrasjoner/criipto.md
 */
export abstract class CriiptoAutentiseringBasis implements IController {

    constructor(public $attrs: IAttributes,
                public $rootScope: IRootScopeService,
                public $timeout: ITimeoutService,
                public $sce: ISCEService,
                public autentisering: Autentisering,
                public criiptoOIDCRessurs: CriiptoOIDCRessurs,
                public meldingTilBruker:MeldingTilBruker,
                public gettextCatalog: gettextCatalog) {
        this.håndterMessageEvent = this.messageEventHandler();
    }

    /** satt via bindings, optional - bruker redirect dersom ikke satt */
    modus: l.CriiptoGuiModus;
    /** satt via bindings, optional - viser alle dersom ikke satt */
    eidmetoder: l.CriiptoIdTilbyder[];
    /** satt via bindings, optional - bruker medium dersom ikke satt */
    tokenvarighet: l.Tokenvarighet;
    /** satt via bindings - optional - benyttes kun ved modus=erstatt */
    criiptoCallbackparams:l.CriiptoCallbackParams;
    /** satt via bindings, optional, merk at tilbyder argumentet ikke er tilgjengelig når vedValgt blir kalt ifbm håndtering criiptoCallbackparams etter redirect */
    vedValgt: (args: { tilbyder?: l.CriiptoIdTilbyder}) => void;
    /** satt via bindings, optional - dersom angitt må denne metoden håndtere videre flyt, dvs at vedAutentisert / vedBrukerIkkeFunnet ikke vil bli kalt*/
    vedCriiptoCallback: (args:{token:string}) => IPromise<void>;
    /** satt via bindings, optional, men MÅ angis dersom vedCriiptoCallback ikke er angitt */
    vedAutentisert: (args: { autentisert: l.AutentisertOkModell }) => void;
    /** satt via bindings, optional, men MÅ angis dersom vedCriiptoCallback ikke er angitt */
    vedBrukerIkkeFunnet: (args: { subject: l.SubjectIdIkkeKnyttetTilBrukerModell }) => void;
    /** satt via bindings, optional */
    vedAvbrutt: () => void;
    /** satt via bindings, optional */
    vedFeil: (args:{feil:any}) => void;

    steg: 'velgeid' | 'velgsvenskeid' | 'visbankiddialog' | 'callbakmottatt' = 'velgeid';

    noBankidLogoBilde = noBankidLogo;
    seBankidLogoBilde = seBankidLogo;

    håndterMessageEvent:any;

    valgtTilbyder: l.CriiptoIdTilbyder; //public for å kunne nås av subklasser
    private autentiseringsrespons: l.CriiptoAutentiseringsResponsModell;
    private criiptovindu;

    erNoBankidTilgjengelig(){
        return _.includes(this.eidmetoder, l.CriiptoIdTilbyder.norsk_bankid_web_og_mobil);
    }

    velgNoBankid() {
        this.visOpenIdAutentiseringGUI(l.CriiptoIdTilbyder.norsk_bankid_web_og_mobil);
    }

    erSeBankidTilgjengelig() {
        return !_.isEmpty(_.intersection(
            this.eidmetoder,
            [l.CriiptoIdTilbyder.svensk_bankid_samedevice, l.CriiptoIdTilbyder.svensk_bankid_anotherdevice, l.CriiptoIdTilbyder.svensk_bankid_anotherdeviceqr]));
    }

    velgSeBankid() {
        this.steg = 'velgsvenskeid';
    }

    velgSeBankidSamedevice() {
        this.visOpenIdAutentiseringGUI(l.CriiptoIdTilbyder.svensk_bankid_samedevice);
    }

    velgSeBankidAnotherdevice() {
        this.visOpenIdAutentiseringGUI(l.CriiptoIdTilbyder.svensk_bankid_anotherdevice);
    }

    velgSeBankidAnotherdeviceqr() {
        this.visOpenIdAutentiseringGUI(l.CriiptoIdTilbyder.svensk_bankid_anotherdeviceqr);
    }

    loginLederplan(){
        debug('openid autentisert - bruker funnet', this.autentiseringsrespons);
        this.autentisering.brukerAutentisertMotServer(this.autentiseringsrespons.lederplanRespons.autentisertBruker);
        return this.vedAutentisert({autentisert: this.autentiseringsrespons.lederplanRespons.autentisertBruker});
    }

    tilbakeTilVelgid(){
        this.steg = 'velgeid';
    }

    avbryt() {
        this.tilbakeTilVelgid();
        if (this.vedAvbrutt) this.vedAvbrutt();
    }

    $onInit(): void {
        if (_.isEmpty(this.eidmetoder))
            this.eidmetoder = [
                l.CriiptoIdTilbyder.norsk_bankid_web_og_mobil,
                l.CriiptoIdTilbyder.svensk_bankid_samedevice,
                l.CriiptoIdTilbyder.svensk_bankid_anotherdevice,
                l.CriiptoIdTilbyder.svensk_bankid_anotherdeviceqr
            ]
    }

    $onDestroy(): void {
        this.avregistrerMeldingslytter();
    }

    $onChanges(changes: IOnChangesObject): void {
        if (changesInneholder(changes, 'criiptoCallbackparams')) {
            debug('$onChanges criiptoCallbackparams angitt', this.criiptoCallbackparams)
            this.håndterCallbackparams(this.criiptoCallbackparams)
        }
    }

    //subklasse kontrakt

    //hjelpemetoder

    /**
     * Lytter til melding som sendes av popup som inneholder open id autentiseringsgui.
     */
    private registrerMeldingslytter() {
        debug('registrerMeldingslytter');
        window.addEventListener("message", this.håndterMessageEvent, false);
    }

    private avregistrerMeldingslytter(){
        window.removeEventListener("message", this.håndterMessageEvent, false);
    }

    private messageEventHandler(){
        return (melding: { origin: string; data: l.CriiptoAutentisertMelding; }) => {
            //mottar mange ulike typer meldinger - håndter kun criiptoAutentisert melding
            debug('håndter message event', melding);
            if (melding.data.type !== 'criiptoAutentisert') return;
            this.$rootScope.$apply(() => this.håndterCallbackparams(melding.data));
            this.criiptovindu.close();
            this.criiptovindu = null;
        };
    }

    /** Meldingslytter for registrering på cordova in app browser.
     * Se https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-inappbrowser/ */
    private cordovaIabMessageEventHandler(){
        return (event:{type:string; data:l.CriiptoAutentisertMelding;}) => {
            debug('håndter message event fra cordova in app browser', event);
            if (event?.data?.type !== 'criiptoAutentisert') return;
            this.$rootScope.$apply(() => this.håndterCallbackparams(event.data));
            this.criiptovindu.close();
            this.criiptovindu = null;
        }
    }

    private cordovaIabExitEventHandler(){
        return (event) => {
            debug('håndter exit event fra cordova in app browser2', event);
            this.$rootScope.$apply(() => {
                //kun avbryt dersom vi er i visbankiddialog
                if (this.criiptovindu != null && this.steg == 'visbankiddialog') this.avbryt();
            });
        }
    }

    visOpenIdAutentiseringGUI(tilbyder: l.CriiptoIdTilbyder) {
        this.steg = 'visbankiddialog';
        this.valgtTilbyder = tilbyder;
        this.kallVedValgt(tilbyder); //skjuler ikke øvrige valg før callback er mottatt

        //for å unngå potensielt problem med popup-blokker i safari lar vi her server redirigere til korrekt url,
        // fremfor å hente url som promise og så åpne windu når promise resolver - som kan føre til popup-blokkering
        const url = this.criiptoOIDCRessurs.redirectTilAuthorizeUrl(this.modus, tilbyder);
        debug('åpner criipto gui', this.modus, url)

        switch (this.modus){
            case l.CriiptoGuiModus.cordova:
                this.visOpenIdAutentiseringGUICordova();
                break;

            case l.CriiptoGuiModus.popup:
                this.registrerMeldingslytter();
                this.criiptovindu = window.open(url, 'criiptogui', '');
                if (this.criiptovindu == null){
                    // popup kan ha blitt blokkert -- orker ikke lage en løsning for å vise melding nå.
                    console.error("Popup blokkert! criipto authhentisering feiler.")
                }
                break;

            default: // l.CriiptoGuiModus.erstatt
                window.location.href = url;
        }
    }

    håndterCallbackparams(params: l.CriiptoCallbackParams) {
        this.criiptoCallbackparams = params;
        this.steg = 'callbakmottatt';
        debug('håndterCallbackparams #### kallVedValgt null!!')
        this.kallVedValgt(null);

        if (!_.isEmpty(params.error)){
            debug('bruker avbrøt bankiddialogen', params)
            this.avbryt();
            return;
        }

        this.håndterCriiptoRespons();
    }

    private visOpenIdAutentiseringGUICordova(){
        this.criiptoOIDCRessurs.authorizeUrl(this.modus, this.valgtTilbyder).$promise
            .then(authorizeUrlModell => {
                debug('åpner criipto gui i cordova', this.modus, this.valgtTilbyder, authorizeUrlModell)
                const url = authorizeUrlModell.criiptoAuthorizeUrl;

                const avbrytTekst = this.gettextCatalog.getString('Avbryt');
                this.criiptovindu = cordova.InAppBrowser.open(url, '_blank', `location=yes,toolbar=yes,toolbartranslucent=no,toolbarcolor=#1565C0,closebuttoncaption=${avbrytTekst}`);

                this.criiptovindu.addEventListener('message', this.cordovaIabMessageEventHandler());
                this.criiptovindu.addEventListener('exit', this.cordovaIabExitEventHandler());
                //merk at det ikke er vits å lytte til loaderror da denne blir kalt når feks bruker avbryter bankiddialog
            })
            .catch(e => {
                //melding har blitt vist av criiptoOIDCRessurs.authorizeUrl
                debug('visOpenIdAutentiseringGUICordova catch', e)
                this.avbryt();
            });
    }

    private håndterCriiptoRespons() {
        debug('håndterCriiptoRespons ######vedCriiptoCallback', this.vedCriiptoCallback)
        if (this.vedCriiptoCallback)
            this.vedCriiptoCallback({token: this.criiptoCallbackparams.code})
                .finally(() => this.steg = 'velgeid'); //vi returnerer alltid til 'velgid' steget
        else
            this.criiptoOIDCRessurs.autentiser(this.innloggingsSpesifikasjon()).$promise
                .then(respons => this.håndterLederplanRespons(respons))
                .catch(e => this.vedFeil({feil: {type: 'CriiptoAutentiseringBasis.håndterCriiptoRespons', e: e}}));
    }

    private innloggingsSpesifikasjon():l.CriiptoTokenInnloggingsSpesifikasjon{
        return {
            kode: this.criiptoCallbackparams.code,
            tokenvarighet: _.defaultTo(this.tokenvarighet, l.Tokenvarighet.medium),
            guiModus: this.modus,
            tilbyder: this.valgtTilbyder,
            styreplanLoginAktivert: false
        }
    }

    private håndterLederplanRespons(respons:l.CriiptoAutentiseringsResponsModell){
        debug('håndterLederplanRespons', respons)
        this.autentiseringsrespons = respons;

        if (respons.erBrukerILederplan) return this.loginLederplan();

        if (respons.lederplanRespons.status === l.AutentiseringStatus.ukjentSubject)
            return this.vedBrukerIkkeFunnet({subject:respons.lederplanRespons.ukjentSubject});

        if (respons.lederplanRespons.status === l.AutentiseringStatus.ugyldigSpesifikasjon){
            this.meldingTilBruker.infomelding(this.gettextCatalog.getString('Din autentiseringssesjon ser ut til å ha løpt ut på tid, vennligst forsøk på nytt.'), Varighet.klikkBort); //Varighet.sideskift funker ikke - pga sideskift til innloggingssiden
            return this.vedAvbrutt();
        }

        debug('håndterCriiptoRespons: ikke håndtert respons', respons)
        this.kallVedFeil({type: 'CriiptoAutentiseringBasis.håndterLederplanRespons.ikkehåndertrespons', respons: respons});
    }

    private kallVedValgt(tilbyder: l.CriiptoIdTilbyder) {
        if (!this.vedValgt) return;
        this.vedValgt({tilbyder: tilbyder});
    }

    private kallVedFeil(feil: any) {
        if (!this.vedFeil) return;
        this.vedFeil({feil: feil});
    }
}

export function criiptoCallbackParamsFraStateParams(stateParams: { [key: string]: any }): l.CriiptoCallbackParams | null {
    const params: l.CriiptoCallbackParams = {code: stateParams['code'], state: stateParams['state'], error: stateParams['error']};
    debug("criiptoCallbackParamsFraStateParams", params);
    return (_.isEmpty(params.code) && _.isEmpty(params.error)) ? null : params;
}

/** bygger og returnerer en SubjectIdIkkeKnyttetTilBrukerModell instans med verdier lest fra $stateParams.
 * Benyttes ifbm redirigering til side som viser dialog for å binde bruker til bankid.
 * Returnerer null dersom kryptertOpenIdIdentitet er tom */
export function lesSubjectIdIkkeKnyttetTilBrukerModellFraStateParams(stateParams: { [key: string]: any }): l.SubjectIdIkkeKnyttetTilBrukerModell | null {
    const params: l.SubjectIdIkkeKnyttetTilBrukerModell = {
        kryptertOpenIdIdentitet: stateParams['kryptertOpenIdIdentitet'],
        openIdNavn: stateParams['openIdNavn'],
        autentiseringsmetode: _.toNumber(stateParams['autentiseringsmetode']),
        tokenvarighet: _.toNumber(stateParams['tokenvarighet'])
    };
    return (_.isEmpty(params.kryptertOpenIdIdentitet)) ? null : params;
}

/*
   private visOpenIdAutentiseringGUI_(tilbyder: l.CriiptoIdTilbyder) {
        this.steg = 'visbankiddialog';
        this.vedValgt({tilbyder: tilbyder}); //skjuler ikke øvrige valg før callback er mottatt

        this.criiptoOIDCRessurs.authorizeUrl(this.modus, tilbyder).$promise
            .then(authorizeUrlModell => {
                debug('åpner criipto gui', this.modus, authorizeUrlModell)
                const url = authorizeUrlModell.criiptoAuthorizeUrl;
                switch (this.modus){
                    case l.CriiptoGuiModus.cordova:
                        this.criiptovindu = cordova.InAppBrowser.open(url, '_blank', '');
                        this.criiptovindu.addEventListener('message', this.cordovaIabMessageEventHandler());
                        this.criiptovindu.addEventListener('exit', this.cordovaIabExitEventHandler());
                        //merk at det ikke er vits å lytte til loaderror da denne blir kalt når feks bruker avbryter bankiddialog
                        break;

                    case l.CriiptoGuiModus.popup:
                        this.registrerMeldingslytter();
                        this.criiptovindu = window.open(url, 'criiptogui', '');
                        break;

                    default: // l.CriiptoGuiModus.erstatt
                        window.location.href = url;
                }
            })
            .catch(e => {
                //melding har blitt vist av criiptoOIDCRessurs.authorizeUrl
                debug('visOpenIdAutentiseringGUI catch', e)
                this.avbryt();
            });
    }

 */
